import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { SortableAttachment } from './SortableAttachment';

export const AttachmentTray = ({ 
  files, 
  onRemove, 
  onRetry, 
  onReorder,
  maxFiles = 5
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = files.findIndex(file => file.id === active.id);
      const newIndex = files.findIndex(file => file.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        mb: 1,
        p: 1,
        backgroundColor: theme.palette.grey[50],
        borderRadius: 1,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Typography 
        variant="caption" 
        sx={{ 
          color: theme.palette.text.secondary,
          mb: 1,
          display: 'block'
        }}
      >
        Attachments ({files.length}/{maxFiles})
      </Typography>
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={files.map(f => f.id)}
          strategy={horizontalListSortingStrategy}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-start',
              // On mobile, enable horizontal scrolling instead of wrapping
              ...(isMobile && {
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'hidden',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 2,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.grey[500],
                  },
                },
              })
            }}
          >
            {files.map((file) => (
              <SortableAttachment
                key={file.id}
                file={file}
                onRemove={onRemove}
                onRetry={onRetry}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      
      {/* Status summary */}
      <Box sx={{ mt: 1 }}>
        {files.some(f => f.status === 'uploading') && (
          <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
            Uploading files...
          </Typography>
        )}
        {files.some(f => f.status === 'error') && (
          <Typography variant="caption" sx={{ color: theme.palette.error.main }}>
            Some uploads failed. Click retry to try again.
          </Typography>
        )}
        {files.length > 0 && files.every(f => f.status === 'done') && (
          <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
            All files uploaded successfully
          </Typography>
        )}
      </Box>
    </Box>
  );
};
